import React, { useEffect, useState } from "react";
import axios from "axios";
import { useSelector, useDispatch } from "react-redux";

export default function PostDetail() {
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [comments, setComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(true);
  const [commentsPage, setCommentsPage] = useState(1);
  const [commentsTotal, setCommentsTotal] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [newComment, setNewComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submittedPending, setSubmittedPending] = useState(false);
  const [userLoading, setUserLoading] = useState(true);
  const [showLoginDialog, setShowLoginDialog] = useState(false);
  const dispatch = useDispatch();
  const userDetails = useSelector((state) => state.user.user);
  const isAuthenticated = userDetails?.id;

  useEffect(() => {
    document.documentElement.setAttribute(
      "data-theme",
      localStorage.getItem("theme") || "light"
    );
  }, []);


  useEffect(() => {
    let cancel = false;
    const controller = new AbortController();
    
    async function fetchData() {
      setLoading(true);
      setCommentsLoading(true);
      setUserLoading(true);
      setErr("");
      setCommentsPage(1);
      
      try {
        const id = window.location.pathname.split("/").pop();
        
        // Fetch both post and comments in parallel
        const [postRes, commentsRes] = await Promise.all([
          axios.get(`/api/community/posts/${id}`, { withCredentials: true, signal: controller.signal }),
          axios.get(`/api/community/posts/${id}/comments?page=1&pageSize=5`, { withCredentials: true, signal: controller.signal })
        ]);
        
        if (!cancel) {
          setItem(postRes?.data || null);
          setComments(commentsRes?.data?.items || []);
          setCommentsTotal(commentsRes?.data?.total || 0);
          setUserLoading(false);
        }
      } catch (e) {
        if (!cancel) {
          setErr("Not found");
          setComments([]);
          setCommentsTotal(0);
          setUserLoading(false);
        }
      } finally {
        if (!cancel) {
          setLoading(false);
          setCommentsLoading(false);
        }
      }
    }
    
    fetchData();
    return () => { cancel = true; controller.abort(); };
  }, [dispatch]);

  async function loadMoreComments() {
    if (loadingMore || !item?._id) return;
    
    setLoadingMore(true);
    try {
      const nextPage = commentsPage + 1;
      const response = await axios.get(
        `/api/community/posts/${item._id}/comments?page=${nextPage}&pageSize=5`,
        { withCredentials: true }
      );
      
      setComments(prev => [...prev, ...(response.data?.items || [])]);
      setCommentsPage(nextPage);
    } catch (e) {
      console.error('Failed to load more comments:', e);
    } finally {
      setLoadingMore(false);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!item?._id) return;
    const content = newComment.trim();
    if (!content) return;

    // Check if user is authenticated
    if (!isAuthenticated) {
      setShowLoginDialog(true);
      return;
    }

    setSubmitting(true);
    setSubmittedPending(false);
    try {
      await axios.post(
        "/api/community/comments",
        { postId: item._id, content },
        { withCredentials: true }
      );
      setNewComment("");
      setSubmittedPending(true);
      setComments((prev) => [
        ...prev,
        {
          _id: `local-${Date.now()}`,
          postId: item._id,
          content,
          status: "PENDING",
          createdAt: new Date().toISOString(),
        },
      ]);
    } catch (_e) {
      // keep input for retry
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-6 max-w-3xl">
        <div className="animate-pulse">
          {/* Title and Avatar skeleton */}
          <div className="flex items-start gap-3 sm:gap-4 mb-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gray-200 rounded-full flex-shrink-0"></div>
            <div className="flex-1">
              <div className="h-6 sm:h-8 bg-gray-200 rounded-lg mb-2 w-3/4"></div>
              <div className="h-3 sm:h-4 bg-gray-200 rounded w-48"></div>
            </div>
          </div>
          
          {/* Content skeleton */}
          <div className="space-y-3 mb-6 sm:mb-8">
            <div className="h-3 sm:h-4 bg-gray-200 rounded"></div>
            <div className="h-3 sm:h-4 bg-gray-200 rounded"></div>
            <div className="h-3 sm:h-4 bg-gray-200 rounded w-5/6"></div>
            <div className="h-3 sm:h-4 bg-gray-200 rounded"></div>
            <div className="h-3 sm:h-4 bg-gray-200 rounded w-4/6"></div>
          </div>

          {/* Comments section skeleton */}
          <div className="h-5 sm:h-6 bg-gray-200 rounded w-20 sm:w-24 mb-3 sm:mb-4"></div>
          <div className="space-y-3 sm:space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="rounded-lg border bg-white p-3 sm:p-4">
                <div className="space-y-2">
                  <div className="h-3 bg-gray-200 rounded"></div>
                  <div className="h-3 bg-gray-200 rounded w-3/4"></div>
                </div>
                <div className="h-3 bg-gray-200 rounded w-32 mt-3"></div>
              </div>
            ))}
          </div>

          {/* Comment form skeleton */}
          <div className="mt-6 sm:mt-8">
            <div className="h-5 sm:h-6 bg-gray-200 rounded w-28 sm:w-32 mb-3"></div>
            <div className="h-20 sm:h-24 bg-gray-200 rounded-lg mb-3"></div>
            <div className="h-8 sm:h-10 bg-gray-200 rounded-lg w-28 sm:w-32"></div>
          </div>
        </div>
      </div>
    );
  }
  
  if (err || !item) return <div className="p-4">Not found</div>;

  // Get author info for avatar
  const authorName = item.isAnonymous ? "Anonymous" : (item.authorId?.name || item.authorId?.username || "User");
  const authorAvatar = item.authorId?.avatar || item.authorId?.profilePicture;
  const firstLetter = authorName.charAt(0).toUpperCase();

  // Determine back URL based on post type
  const getBackUrl = () => {
    if (!item) return '/';
    return item.type === "PRAISE" ? "/praise" : "/prayers";
  };

  return (
    <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-6 max-w-3xl">
      {/* Back Button */}
      <div className="mb-4">
        <a
          href={getBackUrl()}
          className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors duration-200"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to {item?.type === "PRAISE" ? "Praise Reports" : "Prayer Requests"}
        </a>
      </div>

      <div className="flex items-start gap-3 sm:gap-4 mb-4">
        {/* Author Avatar */}
        <div className="flex-shrink-0">
          {authorAvatar && !item.isAnonymous ? (
            <img
              className="w-10 h-10 sm:w-12 sm:h-12 rounded-full object-cover border-2 border-gray-100 shadow-sm"
              src={authorAvatar}
              alt={authorName}
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
          ) : null}
          <div 
            className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center text-sm sm:text-base font-bold text-white shadow-sm border-2 border-gray-100 ${authorAvatar && !item.isAnonymous ? 'hidden' : 'flex'}`}
            style={{
              backgroundColor: item.isAnonymous ? '#6b7280' : '#0074B9'
            }}
          >
            {item.isAnonymous ? '?' : firstLetter}
          </div>
        </div>
        
        {/* Title and Meta Info */}
        <div className="flex-1 min-w-0">
          <h1 className="text-xl sm:text-2xl font-semibold break-words">{item.title}</h1>
          <div className="mt-1 sm:mt-2 text-xs sm:text-sm text-slate-600">
            <span className="font-medium">{authorName}</span> • {new Date(item.createdAt).toLocaleString()}
          </div>
        </div>
      </div>

      <div 
        className="mt-6 sm:mt-8 break-words text-slate-800 text-sm sm:text-base"
        dangerouslySetInnerHTML={{ __html: item.content }}
      />

      <section className="mt-6 sm:mt-8">
        {userLoading ? (
          <div className="animate-pulse">
            <div className="h-5 sm:h-6 bg-gray-200 rounded w-28 sm:w-32 mb-3"></div>
            <div className="h-20 sm:h-24 bg-gray-200 rounded-lg mb-3"></div>
            <div className="h-8 sm:h-10 bg-gray-200 rounded-lg w-28 sm:w-32"></div>
          </div>
        ) : (
          <>
            <h3 className="text-lg font-semibold">Add a comment</h3>
            {submittedPending && (
              <div className="mt-3 rounded border border-yellow-200 bg-yellow-50 p-3 text-yellow-800 text-sm sm:text-base">
                Your comment was submitted and is pending admin approval.
              </div>
            )}
            <form className="mt-3" onSubmit={handleSubmit}>
              <textarea
                className="w-full rounded-lg border p-3 outline-none focus:ring-2 focus:ring-[#0074B9] text-sm sm:text-base"
                rows={4}
                placeholder="Write your comment…"
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
              />
              <div className="mt-3">
                <button
                  type="submit"
                  disabled={submitting || !newComment.trim()}
                  className="rounded-lg bg-[#0074B9] px-3 sm:px-4 py-2 text-white disabled:opacity-50 hover:bg-[#005a9c] transition-all duration-200 text-sm sm:text-base"
                >
                  {submitting ? "Submitting…" : "Post comment"}
                </button>
              </div>
            </form>
          </>
        )}
      </section>

      <section className="mt-6 sm:mt-8">
        <h2 className="text-lg font-semibold">Comments</h2>
        {commentsLoading ? (
          <div className="mt-3 sm:mt-4 space-y-3 sm:space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="animate-pulse rounded-lg border bg-white p-3 sm:p-4">
                <div className="space-y-2">
                  <div className="h-3 bg-gray-200 rounded"></div>
                  <div className="h-3 bg-gray-200 rounded w-3/4"></div>
                </div>
                <div className="h-3 bg-gray-200 rounded w-32 mt-3"></div>
              </div>
            ))}
          </div>
        ) : comments.filter(c => c.status === 'APPROVED').length === 0 ? (
          <div className="mt-3 text-slate-600 text-sm sm:text-base">No comments yet.</div>
        ) : (
          <>
            <div className="mt-3 sm:mt-4 space-y-1">
              {comments.filter(c => c.status === 'APPROVED').map((c) => {
                const authorName = c.authorId?.name || "User";
                const authorAvatar = c.authorId?.avatar || c.authorId?.profilePicture;
                const firstLetter = authorName.charAt(0).toUpperCase();
                const timeAgo = c.createdAt ? new Date(c.createdAt).toLocaleString() : "";
                
                return (
                  <div key={c._id} className="flex gap-3 p-3 rounded-lg">
                    {/* Avatar */}
                    <div className="flex-shrink-0">
                      {authorAvatar ? (
                        <img
                          className="w-8 h-8 rounded-full object-cover"
                          src={authorAvatar}
                          alt={authorName}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div 
                        className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white ${authorAvatar ? 'hidden' : 'flex'}`}
                        style={{
                          backgroundColor: '#0074B9'
                        }}
                      >
                        {firstLetter}
                      </div>
                    </div>
                    
                    {/* Comment Content */}
                    <div className="flex-1 min-w-0">
                      <div className="bg-gray-100 rounded-2xl px-3 py-2 inline-block">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-semibold text-gray-900">{authorName}</span>
                          <span className="text-xs text-gray-500">•</span>
                          <span className="text-xs text-gray-500">{timeAgo}</span>
                        </div>
                        <div className="text-sm text-gray-800 whitespace-pre-line break-words">{c.content}</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* Load More Button */}
            {comments.length < commentsTotal && (
              <div className="mt-4 text-center">
                <button
                  onClick={loadMoreComments}
                  disabled={loadingMore}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                >
                  {loadingMore ? "Loading..." : "Load more comments"}
                </button>
              </div>
            )}
          </>
        )}
      </section>

      {/* Login Dialog */}
      {showLoginDialog && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowLoginDialog(false);
            }
          }}
        >
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Login Required</h3>
            <p className="text-gray-600 mb-6">
              You need to be logged in to leave a comment. Please log in to continue.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowLoginDialog(false)}
                className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors duration-200"
              >
                Cancel
              </button>
              <a
                href={`/login?callbackUrl=${encodeURIComponent(window.location.pathname)}`}
                className="flex-1 px-4 py-2 bg-[#0074B9] text-white rounded-lg hover:bg-[#005a9c] transition-colors duration-200 text-center"
              >
                Login
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
